if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.submitButtonCurrent = this.submitButton.querySelector('span').innerHTML;
        this.submitButtonSpinner = this.submitButton.querySelector('.loading__spinner');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Sticky ATC button lives outside this element and submits the same form via
        // its form="" attribute, so it isn't found by the querySelector above. Keep it
        // in lockstep with the primary button's loading/disabled state to avoid a
        // double-submit if it's clicked while a request from the other button is pending.
        this.stickySubmitButton = document.getElementById(`StickyATCSubmitButton-${this.dataset.sectionId}`);
        if (this.stickySubmitButton) {
          this.stickySubmitButtonText = this.stickySubmitButton.querySelector('span');
          this.stickySubmitButtonCurrent = this.stickySubmitButtonText.innerHTML;
          this.stickySubmitButtonSpinner = this.stickySubmitButton.querySelector('.loading__spinner');
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (
          this.submitButton.getAttribute('aria-disabled') === 'true' ||
          this.stickySubmitButton?.getAttribute('aria-disabled') === 'true'
        )
          return;

        this.handleErrorMessage();

        [this.submitButton, this.stickySubmitButton].forEach((button) => {
          if (!button) return;
          button.setAttribute('aria-disabled', true);
          button.classList.add('loading');
        });
        this.submitButtonSpinner.classList.remove('hidden');
        this.stickySubmitButtonSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.stickySubmitButton?.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              this.stickySubmitButton?.removeAttribute('aria-disabled');
            }
            this.submitButtonSpinner.classList.add('hidden');
            this.stickySubmitButtonSpinner?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (this.errorMessageWrapper) {
          this.errorMessage =
            this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
          if (errorMessage) {
            this.errorMessage.textContent = errorMessage;
          }
        }

        // Sticky ATC error wrapper lives outside this element (see stickySubmitButton
        // above), so mirror the same toggle there via a document-level lookup.
        this.stickyErrorMessageWrapper =
          this.stickyErrorMessageWrapper ||
          document.getElementById(`StickyErrorMessage-${this.dataset.sectionId}`);
        if (this.stickyErrorMessageWrapper) {
          this.stickyErrorMessage =
            this.stickyErrorMessage || this.stickyErrorMessageWrapper.querySelector('.sticky-atc__error-message');
          this.stickyErrorMessageWrapper.toggleAttribute('hidden', !errorMessage);
          if (errorMessage) {
            this.stickyErrorMessage.textContent = errorMessage;
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          if (this.stickySubmitButton) {
            this.stickySubmitButton.setAttribute('disabled', 'disabled');
            if (text) this.stickySubmitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.submitButtonCurrent;
          if (this.stickySubmitButton) {
            this.stickySubmitButton.removeAttribute('disabled');
            this.stickySubmitButtonText.textContent = this.stickySubmitButtonCurrent;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
